/**
 * Outreach error copy that depends on what the admin was doing, plus the codes that had no copy before
 * (owner 2026-10-12). One code can surface from a save and from a send, so the words are keyed by both.
 */
import { addressGuardHeldCopy } from './sendGuardCopy';
import { AI_HOOK_IN_SUBJECT_COPY, AI_HOOK_REPEATED_COPY, TEMPLATE_STEP_LIMIT_COPY } from './templateValidation';

/** 'send' is the default reading; 'save' is a draft or composer save; 'template' is a template edit. */
export type ErrorContext = 'send' | 'save' | 'template';

type Details = Record<string, unknown> | undefined;
export type CopyFn = (d?: Details) => string;

const SUBJECT_TOO_LONG = 'The subject is too long. Shorten it and try again.';

const tooLong = (verb: string): CopyFn => (d) => (d?.field === 'subject' ? SUBJECT_TOO_LONG : `This message is too long to ${verb}. Shorten it and try again.`);

/** Only what SEND_ERROR_LABELS can't say as a plain string; the rest of the send wording lives there. */
const SEND_COPY: Record<string, CopyFn> = {
  TEXT_TOO_LONG: tooLong('send'),
  REQUEST_TOO_LARGE: () => 'This message is too large to send. Shorten it and try again.',
  INVALID_SUGGESTION_JOB_ID: () => "This reply couldn't be sent. Refresh the thread and try again.",
  MESSAGE_HAS_NO_HTML: () => "This email can't be sent because its formatted copy is missing. Edit the text and send it again, or stop it and write a new one.",
};

const SAVE_COPY: Record<string, CopyFn> = {
  BODY_REQUIRED: () => 'Add a message before saving.',
  DRAFT_BODY_REQUIRED: () => 'Add a message before saving.',
  DRAFT_SUBJECT_REQUIRED: () => 'Add a subject before saving. The first email needs one.',
  DRAFT_BODY_TOO_LONG: () => 'This email is over the 2,000-character limit. Shorten it to save.',
  DRAFT_SUBJECT_TOO_LONG: () => 'This subject is over the 60-character limit. Shorten it to save.',
  FORMATTING_WITHOUT_TEXT: () => 'This message has formatting but no words. Add a message before saving.',
  TEXT_TOO_LONG: tooLong('save'),
  REQUEST_TOO_LARGE: () => 'This draft is too large to save. Shorten it and try again.',
  INVALID_SUGGESTION_JOB_ID: () => "This draft couldn't be saved. Refresh the thread and try again.",
  MESSAGE_HAS_NO_HTML: () => "This email's formatted copy is missing. Change the text and save it again before you send it.",
};

const TEMPLATE_COPY: Record<string, CopyFn> = {
  AI_HOOK_IN_SUBJECT: () => AI_HOOK_IN_SUBJECT_COPY,
  AI_HOOK_REPEATED: () => AI_HOOK_REPEATED_COPY,
  FIRST_TOUCH_LINK: () => "Step 1 is the first email and can't contain a link. Remove it, or move it to a later step.",
  TEMPLATE_ARCHIVED: () => "This template is archived, so it can't be changed or picked.",
};

const BY_CONTEXT: Record<ErrorContext, Record<string, CopyFn>> = { send: SEND_COPY, save: SAVE_COPY, template: TEMPLATE_COPY };

const pick = (map: Record<string, CopyFn>, code: string): CopyFn | undefined => (Object.hasOwn(map, code) ? map[code] : undefined);

/** The override for this code in this context, if the context words it differently. A template edit is a save. */
export function contextCopy(code: string, context: ErrorContext): CopyFn | undefined {
  return pick(BY_CONTEXT[context], code) ?? (context === 'template' ? pick(SAVE_COPY, code) : undefined);
}

const withReason = (lead: string): CopyFn => (d) => (typeof d?.reason === 'string' && d.reason ? `${lead} — ${d.reason}.` : `${lead}.`);

const STALE_STATE = 'Refresh the thread to see where it stands.';

export const FIRST_TOUCH_WIZARD_ONLY_COPY = 'First emails go out through Bulk send. Open Bulk send to review and send it.';
export const WIZARD_BATCH_EXPIRED_COPY = 'This batch has expired. Go back to step 3 to redraft.';
export const ENTRY_NO_OUTREACH_HISTORY_COPY = "This creator has no outreach history in this campaign, so a campaign-only email or time zone can't be set. Remove it to save the rest.";
export const ENTRY_NOT_SOURCED_COPY = "This creator's outreach history is incomplete, so a campaign-only email or time zone can't be set yet. Remove it to save the rest, and ask an engineer to repair the entry.";

/** "13 Oct, 09:00" in the viewer's own clock, or null for a value that isn't a time. */
function retryWhen(value: unknown): string | null {
  const at = typeof value === 'string' ? new Date(value) : null;
  if (!at || Number.isNaN(at.getTime())) return null;
  return at.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' });
}

/** Codes that had no copy until 2026-10-12. Read after the context and the negotiation/send maps. */
export const MORE_ERROR_COPY: Record<string, CopyFn> = {
  DRAFT_ALREADY_PENDING: () => 'An earlier draft for this creator is still waiting. Retry writes a new one.',
  RECIPIENT_NOT_ALLOWLISTED: () => "This test environment only sends to allowed test addresses. Set this creator's email override to an allowed test address, then try again.",
  FIRST_TOUCH_WIZARD_ONLY: () => FIRST_TOUCH_WIZARD_ONLY_COPY,
  INVALID_WIZARD_BATCH_ID: () => WIZARD_BATCH_EXPIRED_COPY,
  SEND_NOW_WITH_SCHEDULE: () => 'Choose Send now or a scheduled time, not both.',
  COUNTER_AFTER_APPROVAL: () => 'The brand has approved this creator — change the price with Agree rate instead.',
  COUNTER_NEEDS_MAILED_QUOTE: () => 'There is no price from the creator in this thread to counter. Write a normal reply, or record a price agreed elsewhere with Agree rate.',
  MESSAGE_NOT_CANCELLABLE: () => "This message already went out or was stopped, so it can't be stopped now.",
  MESSAGE_NOT_RESENDABLE: () => `Only a failed, held or unconfirmed message can be resent. ${STALE_STATE}`,
  MESSAGE_NOT_UNKNOWN: () => `Only a message whose delivery is unconfirmed can be marked as sent. ${STALE_STATE}`,
  MESSAGE_NOT_FOUND: () => 'That message is gone. Refresh the thread.',
  REASON_REQUIRED: () => "Say what you checked, for example the mailbox's Sent folder.",
  ENTRY_ALREADY_HAS_CONTACT: () => 'This creator already has an email on file.',
  ENTRY_NO_OUTREACH_HISTORY: () => ENTRY_NO_OUTREACH_HISTORY_COPY,
  ENTRY_NOT_SOURCED: () => ENTRY_NOT_SOURCED_COPY,
  ENTRY_NOT_FOUND: () => "We can't find this creator. Refresh the page.",
  CAMPAIGN_ALREADY_PAUSED: () => 'Outreach is already paused for this campaign.',
  CAMPAIGN_NOT_PAUSED: () => 'Outreach is already running for this campaign.',
  ATTACHMENT_TOO_LARGE: () => 'This attachment is too large to download here. Open it in the mailbox instead.',
  CONTENT_TYPE_NOT_ALLOWED: () => "This type of attachment can't be downloaded here. Open it in the mailbox instead.",
  IDEMPOTENCY_KEY_REQUIRED: () => "That didn't go through. Refresh the page and try again.",
  IDEMPOTENCY_KEY_REUSED: () => 'This may already have gone through. Refresh and check before you try again.',
  IDEMPOTENCY_UNAVAILABLE: () => "We couldn't confirm whether that went through. Refresh and check before you try again.",
  REQUEST_IN_PROGRESS: () => 'This is still being processed. Wait a moment, then refresh.',
  INVALID_REASON: () => "That reason isn't allowed here. Pick another.",
  SETTINGS_CHANGED: () => 'These settings changed in another session. Check the template and try again.',
  TEMPLATE_LOCKED: () => "This campaign has already sent its first email, so its template can't change.",
  TEMPLATE_NAME_TAKEN: () => 'A template with this name already exists here. Pick another name.',
  TEMPLATE_NOT_FOUND: () => 'This template no longer exists. Go back to Templates.',
  TEMPLATE_STEP_IN_USE: () => "A step you removed has already been sent, so it can't be removed. Keep the step, or archive the template and make a new one.",
  TEMPLATE_FOREIGN_SCOPE: () => "This template belongs to another brand or campaign, so it can't be used here.",
  TEMPLATE_GLOBAL_READONLY: () => 'Global templates are read-only. Duplicate it into this campaign to change it.',
  INVALID_TEMPLATE_NAME: () => 'Template name must be 1 to 120 characters.',
  INVALID_TEMPLATE_KIND: () => "This template can't be used for that. Pick a sequence template.",
  INVALID_TEMPLATE_SCOPE: () => 'A template can only be saved to this campaign or its brand.',
  PARENT_NOT_FOUND: () => 'The template this was duplicated from no longer exists. Start a new template instead.',
  PARENT_SCOPE_MISMATCH: () => "The template this was duplicated from can't be used in this campaign.",
  NO_FIELDS: () => 'Nothing changed, so there was nothing to save.',
  INVALID_STEPS: (d) => (typeof d?.reason === 'string' && /at most \d+ steps/.test(d.reason) ? TEMPLATE_STEP_LIMIT_COPY : withReason('Check the template steps')(d)),
  INVALID_SIGNATURE: withReason('Check the signature'),
  UNKNOWN_MERGE_TAG: (d) => {
    const raw = Array.isArray(d?.tags) ? (d.tags as unknown[]).filter((t): t is string => typeof t === 'string') : [];
    const tags = [...new Set(raw)];
    if (tags.length === 0) return "A merge tag in this template isn't one we know. Fix the spelling or remove it.";
    const list = tags.map((t) => `{${t}}`).join(', ');
    return tags.length === 1 ? `${list} isn't a merge tag. Fix the spelling or remove it.` : `${list} aren't merge tags. Fix the spelling or remove them.`;
  },
  POOL_WARMING: () => "Open tracking can't be turned on until every send mailbox has finished warming up.",
  ADDRESS_GUARD_OVERRIDE_REQUIRED: (d) => addressGuardHeldCopy(d?.held_reason),
  OVERRIDE_ADDRESS_CHANGED: (d) =>
    typeof d?.to_address === 'string' && d.to_address
      ? `The creator's email is now ${d.to_address}, not the address you sent anyway for. Check it, then use Send anyway again if it's still right.`
      : "The creator's email changed since you looked. Refresh the thread, check the address, then use Send anyway again if it's still right.",
  ADDRESS_GUARD_OVERRIDE_ADMIN_ONLY: () => "Only an admin can send anyway to a risky address. Ask an admin, or change the creator's email.",
  EXPECTED_TO_REQUIRED: () => "This email has no valid address to send to. Check the creator's email, then try again.",
  FOLLOWUP_DAILY_CAP_REACHED: (d) => {
    const when = retryWhen(d?.retry_at);
    return `This campaign's daily follow-up limit is reached, so this follow-up goes out ${when ? `at ${when}` : 'once the limit resets'}.`;
  },
};

/** Go's catch-all codes: Go's own sentence (which names the field or reason) wins when there is one. */
export const GENERIC_ERROR_COPY: Record<string, string> = {
  BAD_REQUEST: "The server couldn't read that request. Refresh the page and try again.",
  INVALID_REQUEST: "The server didn't accept that request. Refresh the page and try again.",
  NOT_FOUND: 'That is no longer here. Refresh the page.',
  FORBIDDEN: "You don't have permission to do that.",
  CONFLICT: 'This changed while you were working. Refresh and try again.',
};

/** Go's sentences for these say nothing useful ("internal error"), so the app's copy always wins. */
export const OPAQUE_ERROR_COPY: Record<string, string> = {
  INTERNAL_ERROR: 'Something went wrong on our side. Try again in a moment.',
  OUTREACH_UNAVAILABLE: "Outreach isn't available right now. Try again in a moment.",
  UNAUTHORIZED: 'Your session has expired. Sign in again.',
};
